// Shared helper for the "large image" advisory shown in the Product Editor
// upload slots. Frontend-only, soft recommendation — never blocks the upload.

// Images above this size trigger a gentle "consider a smaller image" note.
export const LARGE_IMAGE_BYTES = 1024 * 1024; // 1 MB

/** Format a byte count as a short human-readable string (e.g. "2.4 MB"). */
export function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Return an advisory string when the file is larger than the recommended
 * threshold, or null when it's fine. Accepts a File or any object with a
 * numeric `size` (bytes).
 */
export function largeImageWarning(file) {
  const size = file && typeof file.size === "number" ? file.size : 0;
  if (size <= LARGE_IMAGE_BYTES) return null;
  return `This image is large (${formatBytes(
    size
  )}). We recommend using a smaller image for faster loading.`;
}

/**
 * Estimate the decoded byte size of a base64 data URL (e.g. an uploaded image
 * held as `data:image/png;base64,...`). Returns 0 for non-data URLs since we
 * can't know a remote asset's size on the client.
 */
export function dataUrlBytes(src) {
  if (typeof src !== "string" || !src.startsWith("data:")) return 0;
  const comma = src.indexOf(",");
  if (comma === -1) return 0;
  const b64 = src.slice(comma + 1);
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

/**
 * Advisory for an already-uploaded image referenced by its (data) URL. Works
 * after a save + reload because the size is derived from the URL itself.
 * Returns null when the image is within the recommended size or not a data URL.
 */
export function largeImageWarningForSrc(src) {
  const size = dataUrlBytes(src);
  if (size <= LARGE_IMAGE_BYTES) return null;
  return `Large image (${formatBytes(
    size
  )}) — a smaller image is recommended for faster loading.`;
}
